import React from 'react';
import {WeatherService} from '../api';
import type {WarningAreasResponse} from '../api/types';
import useApiClient from './useApiClient';

export type UseWarningAreasParams = {
  /** The warning on screen; null or undefined fetches nothing. */
  id: number | null | undefined;
  /** The place the detail is about; marks its area `own`. Optional. */
  lat?: number | null;
  lon?: number | null;
};

export type UseWarningAreasResult = {
  /** The areas of THIS warning at THIS point; null until they arrive. */
  data: WarningAreasResponse | null;
  /** A fetch for the current warning and point is in flight. */
  loading: boolean;
  /**
   * The fetch failed, 404 included (a deleted warning), or answered without
   * a feature collection. The app then shows the list-only fallback.
   */
  error: boolean;
};

type Settled = {key: string} & (
  {data: WarningAreasResponse; error: false} | {data: null; error: true}
);

const usable = (response: unknown): response is WarningAreasResponse =>
  Array.isArray(
    (response as WarningAreasResponse | null | undefined)?.areas?.features,
  );

/**
 * The areas of the warning on the detail screen (`warnings/<id>/areas/`):
 * fetched when it opens and whenever the warning or point changes, never
 * polled, and silent (no error snackbar: the map is an extra, the list-only
 * fallback stands in for it).
 *
 * Keyed by warning and point, like `useWeatherWarningsSync`: a response is
 * kept only if its request is still the current one when it settles, and
 * read back only under the key it was made for, so the previous warning's
 * areas never show (or linger) on the next one (the bug class fixed in FE
 * !726).
 */
const useWarningAreas = ({
  id,
  lat,
  lon,
}: UseWarningAreasParams): UseWarningAreasResult => {
  const {fetch} = useApiClient();
  const hasPoint =
    typeof lat === 'number' &&
    typeof lon === 'number' &&
    Number.isFinite(lat) &&
    Number.isFinite(lon);
  // Half a point is no point: neither half then re-runs the fetch.
  const pointLat = hasPoint ? lat : null;
  const pointLon = hasPoint ? lon : null;
  const key = id != null ? `${id}|${pointLat ?? ''}|${pointLon ?? ''}` : null;

  const [settled, setSettled] = React.useState<Settled | null>(null);

  React.useEffect(() => {
    if (key === null) {
      return undefined;
    }
    // Cleared when the warning or point changes (or the screen unmounts):
    // a response that settles after that is not for what is on screen, even
    // when the user has since come back to the same warning.
    let live = true;
    const settle = (result: Settled) => {
      if (live) {
        setSettled(result);
      }
    };
    fetch(
      WeatherService.warningAreas({
        // A key implies an id.
        id: id as number,
        lat: pointLat,
        lon: pointLon,
      }),
      {silent: true, ignore404: true},
    ).then(
      (response) =>
        settle(
          usable(response)
            ? {key, data: response, error: false}
            : {key, data: null, error: true},
        ),
      () => settle({key, data: null, error: true}),
    );
    return () => {
      live = false;
      // A result outlives neither its request nor its key: coming back to a
      // warning whose last visit failed reads "loading", not that failure.
      setSettled(null);
    };
  }, [key, id, pointLat, pointLon, fetch]);

  return React.useMemo(() => {
    const mine = key !== null && settled?.key === key ? settled : null;
    return {
      data: mine?.data ?? null,
      loading: key !== null && mine === null,
      error: mine?.error ?? false,
    };
  }, [key, settled]);
};

export default useWarningAreas;
